import { baseApi } from "./api";
import type { Message, Conversation, SendMessagePayload, RecipientType } from "@nicsmith/shared_types";

interface ConversationMessagesResponse {
  conversation: Conversation | null;
  messages: Message[];
  lastEvaluatedKey?: Record<string, unknown>;
}

interface ConversationsListResponse {
  items: Conversation[];
  lastEvaluatedKey?: Record<string, unknown>;
}

interface UnreadCountResponse {
  unread_count: number;
  unread_count_admin?: number;
  unread_count_curator?: number;
}

export const messagesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // List all conversations (admin inbox)
    listConversations: builder.query<ConversationsListResponse, { limit?: number } | void>({
      query: (params) => ({
        url: "/messages/conversations",
        params: { limit: params ? params.limit ?? 20 : 20 },
      }),
      providesTags: ["Conversations"],
    }),

    // Get conversation + messages
    getConversationMessages: builder.query<
      ConversationMessagesResponse,
      { userUid: string; limit?: number; recipientType?: RecipientType }
    >({
      query: ({ userUid, limit = 50, recipientType }) => ({
        url: `/messages/conversations/${userUid}`,
        params: { limit, ...(recipientType ? { recipient_type: recipientType } : {}) },
      }),
      providesTags: (_result, _error, { userUid, recipientType }) => [
        { type: "Messages", id: `${userUid}#${recipientType ?? "admin"}` },
      ],
    }),

    // Send a message
    sendMessage: builder.mutation<
      Message,
      { userUid: string; payload: SendMessagePayload; recipientType?: RecipientType }
    >({
      query: ({ userUid, payload, recipientType }) => ({
        url: `/messages/conversations/${userUid}`,
        method: "POST",
        body: { ...payload, ...(recipientType ? { recipient_type: recipientType } : {}) },
      }),
      invalidatesTags: (_result, _error, { userUid, recipientType }) => [
        "Conversations",
        { type: "Messages", id: `${userUid}#${recipientType ?? "admin"}` },
        "UnreadCount",
      ],
    }),

    // Mark conversation as read
    markAsRead: builder.mutation<
      { success: boolean },
      { userUid: string; recipientType?: RecipientType }
    >({
      query: ({ userUid, recipientType }) => ({
        url: `/messages/conversations/${userUid}/read`,
        method: "PUT",
        params: recipientType ? { recipient_type: recipientType } : {},
      }),
      invalidatesTags: (_result, _error, { userUid, recipientType }) => [
        "Conversations",
        { type: "Messages", id: `${userUid}#${recipientType ?? "admin"}` },
        "UnreadCount",
      ],
    }),

    // Get unread count for sidebar badge
    getUnreadCount: builder.query<UnreadCountResponse, void>({
      query: () => "/messages/unread",
      providesTags: ["UnreadCount"],
    }),
  }),
  overrideExisting: true,
});

export const {
  useListConversationsQuery,
  useGetConversationMessagesQuery,
  useSendMessageMutation,
  useMarkAsReadMutation,
  useGetUnreadCountQuery,
} = messagesApi;
